/**
 * <valco-google-store-locator>
 *
 * Server-rendered list of dealer cards is the no-JS baseline. This component
 * upgrades it with: Google map (lazy-loaded), search, type-filter chips,
 * "near me" geolocation, list↔map sync, and a mobile list/map tab toggle.
 *
 * Reads its dealer payload from a child <script type="application/json"
 * data-dealers> emitted by the Liquid section. All UI strings come from
 * the host theme via Liquid (no in-component i18n).
 */

(function () {
  if (customElements.get('valco-google-store-locator')) return;

  const SCRIPT_LOAD_CACHE = new Map();
  const AUTH_FAILURE_EVENT = 'valco-google-maps-auth-failure';
  let mapsPromise;
  let authFailed = false;
  const previousAuthFailure = window.gm_authFailure;
  window.gm_authFailure = function () {
    authFailed = true;
    window.dispatchEvent(new Event(AUTH_FAILURE_EVENT));
    if (typeof previousAuthFailure === 'function') previousAuthFailure();
  };

  function timeout(promise, milliseconds = 20000) {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Map load timed out')), milliseconds); }),
    ]).finally(() => clearTimeout(timer));
  }

  function loadScriptOnce(url) {
    if (SCRIPT_LOAD_CACHE.has(url)) return SCRIPT_LOAD_CACHE.get(url);
    const script = document.createElement('script');
    const promise = timeout(new Promise((resolve, reject) => {
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Map asset unavailable'));
      document.head.appendChild(script);
    })).catch((error) => {
      script.remove();
      SCRIPT_LOAD_CACHE.delete(url);
      throw error;
    }).finally(() => { script.onload = script.onerror = null; });
    SCRIPT_LOAD_CACHE.set(url, promise);
    return promise;
  }

  function loadGoogleMaps(key) {
    if (authFailed) return Promise.reject(new Error('Map authentication unavailable'));
    if (!mapsPromise) {
      mapsPromise = (async () => {
        if (!window.google?.maps?.importLibrary) {
          const url = new URL('https://maps.googleapis.com/maps/api/js');
          url.searchParams.set('key', key);
          url.searchParams.set('v', 'weekly');
          url.searchParams.set('loading', 'async');
          url.searchParams.set('callback', '__valcoGoogleMapsReady');
          url.searchParams.set('language', document.documentElement.lang || 'fi');
          await timeout(new Promise((resolve, reject) => {
            window.__valcoGoogleMapsReady = resolve;
            loadScriptOnce(url.href).catch(reject);
          })).finally(() => { window.__valcoGoogleMapsReady = () => {}; });
        }
        if (authFailed || !window.google?.maps?.importLibrary) throw new Error('Map API unavailable');
        await timeout(Promise.all([
          window.google.maps.importLibrary('maps'),
          window.google.maps.importLibrary('marker'),
        ]));
        if (authFailed) throw new Error('Map authentication unavailable');
      })();
    }
    return mapsPromise;
  }

  function coordinates(lat, lng) {
    if (lat == null || lng == null || String(lat).trim() === '' || String(lng).trim() === '') return null;
    const point = { lat: Number(lat), lng: Number(lng) };
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng) || Math.abs(point.lat) > 90 || Math.abs(point.lng) > 180) return null;
    // The existing dealer data uses 0,0 as the missing-location sentinel.
    return point.lat === 0 && point.lng === 0 ? null : point;
  }

  function haversineKm(a, b) {
    const R = 6371;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(Math.min(1, h)));
  }

  function formatDistance(km) {
    if (!isFinite(km)) return '';
    if (km < 1) return Math.round(km * 1000) + ' m';
    if (km < 10) return km.toFixed(1) + ' km';
    return Math.round(km) + ' km';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  class ValcoGoogleStoreLocator extends HTMLElement {
    constructor() {
      super();
      this._dealers = [];
      this._activeId = null;
      this._activeTypes = new Set();
      this._fittingActive = false;
      this._searchTerm = '';
      this._userLatLng = null;
      this._map = null;
      this._cluster = null;
      this._userMarker = null;
      this._markersById = new Map();
      this._cardsById = new Map();
      this._countNode = null;
      this._emptyNode = null;
      this._countTemplate = '';
      this._initializing = false;
      this._generation = 0;
      this._mapLoading = false;
      this._mapUnavailable = false;
      this._nearViewport = false;
      this._ready = false;
      this._mapListeners = [];
      this._pendingSelection = null;
      this._visibleDealerIds = new Set();
    }

    connectedCallback() {
      if (this._initializing) return;
      this._initializing = true;
      this._controller = new AbortController();
      const generation = ++this._generation;
      this._initialize(generation).catch(() => {
        if (this._alive(generation)) this._handleMapUnavailable();
      });
    }

    _alive(generation) {
      return this.isConnected && generation === this._generation;
    }

    disconnectedCallback() {
      ++this._generation;
      this._controller?.abort();
      this._observer?.disconnect();
      this._resizeObserver?.disconnect();
      this._disposeMap();
      this._ready = false;
      this._initializing = false;
      this._mapLoading = false;
      this._nearViewport = false;
      this._pendingSelection = null;
    }

    _disposeMap() {
      clearTimeout(this._mapRenderTimer);
      this._infoWindow?.close();
      this._mapListeners.splice(0).forEach((listener) => listener.remove());
      this._cluster?.clearMarkers();
      this._cluster?.setMap(null);
      this._markersById.forEach((marker) => {
        window.google?.maps?.event.clearInstanceListeners(marker);
        marker.map = null;
      });
      this._markersById.clear();
      if (this._userMarker) this._userMarker.map = null;
      if (this._map) window.google?.maps?.event.clearInstanceListeners(this._map);
      this.querySelector('[data-map]')?.replaceChildren();
      this._map = this._cluster = this._userMarker = this._infoWindow = null;
    }

    async _initialize(generation) {
      const dataEl = this.querySelector('script[data-dealers]');
      if (!dataEl) {
        this._handleMapUnavailable();
        return;
      }
      try {
        this._dealers = JSON.parse(dataEl.textContent || '[]');
        if (!Array.isArray(this._dealers)) {
          this._handleMapUnavailable();
          return;
        }
      } catch (e) {
        this._handleMapUnavailable();
        return;
      }

      await this._loadRemainingPages(generation);
      if (!this._alive(generation)) return;

      this._countNode = this.querySelector('[data-count]');
      this._emptyNode = this.querySelector('[data-empty]');
      this._countTemplate = this._countTemplate || (this._countNode ? this._countNode.textContent.trim() : '');
      this._cardsById.clear();

      this.querySelectorAll('.valco-sl__card').forEach((card) => {
        const id = Number(card.dataset.dealerId);
        this._cardsById.set(id, card);
      });

      // Theme-editor reconnection may provide corrected configuration after a
      // previous missing-key/Map-ID failure. Keep genuine auth failures closed.
      if (this._mapUnavailable && !authFailed && this.dataset.googleMapsKey &&
          this.dataset.googleMapId && this.dataset.googleMapId !== 'DEMO_MAP_ID' && this.dataset.clusterJs) {
        this._mapUnavailable = false;
        this.dataset.mapState = 'idle';
        const mapPane = this.querySelector('.valco-sl__pane--map');
        if (mapPane) mapPane.hidden = false;
        const tabs = this.querySelector('.valco-sl__tabs');
        if (tabs) tabs.hidden = false;
        for (const dealer of this._dealers) {
          const button = this._cardsById.get(Number(dealer.id))?.querySelector('[data-show-on-map]');
          if (button) button.hidden = Boolean(dealer.online) || !coordinates(dealer.lat, dealer.lng);
        }
      }

      this._sortDealersAndCards();

      const search = this.querySelector('[data-search]');
      if (search) this._searchTerm = (search.value || '').trim().toLowerCase();
      const nearMe = this.querySelector('[data-near-me]');
      if (nearMe) nearMe.disabled = false;
      this._bindEvents();
      this._renderTypeChips();
      this._applyFilters();
      this._applyMobileView(this.dataset.defaultMobileView || 'list');

      if (window.matchMedia('(prefers-reduced-data: reduce)').matches && !this.hasAttribute('data-force-map')) {
        // Leave the no-JS list rendered; skip loading the map bundle.
        this._handleMapUnavailable();
        return;
      }

      this._ready = true;
      window.addEventListener(AUTH_FAILURE_EVENT, () => this._handleMapUnavailable(), { signal: this._controller.signal });
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          this._nearViewport = entries.some((entry) => entry.isIntersecting);
          this._maybeBootMap();
        }, { rootMargin: '200px' });
        this._observer.observe(this);
      } else {
        this._nearViewport = true;
      }
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._maybeBootMap());
        this._resizeObserver.observe(this.querySelector('[data-map]') || this);
      }
      window.addEventListener('resize', () => this._maybeBootMap(), { signal: this._controller.signal });
      this._maybeBootMap();
    }

    async _loadRemainingPages(generation) {
      const totalPages = Number(this.dataset.paginationPages) || 1;
      const currentPage = Number(this.dataset.paginationPage) || 1;
      const pageParam = this.dataset.paginationParam || 'page';
      const sectionId = this.dataset.sectionId;
      const list = this.querySelector('[data-list]');
      if (totalPages <= 1 || !sectionId || !list) {
        this.dataset.paginationState = 'complete';
        return;
      }

      const dealerIds = new Set(this._dealers.map((dealer) => Number(dealer.id)));
      const cardIds = new Set(
        Array.from(list.querySelectorAll('.valco-sl__card')).map((card) => Number(card.dataset.dealerId))
      );
      let failed = false;

      for (let page = 1; page <= totalPages; page++) {
        if (!this._alive(generation)) return;
        if (page === currentPage) continue;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set(pageParam, String(page));
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            signal: this._controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error('HTTP ' + response.status);

          const markup = await response.text();
          if (!this._alive(generation)) return;
          const documentPage = new DOMParser().parseFromString(markup, 'text/html');
          const sectionPage = documentPage.querySelector('valco-google-store-locator');
          const dataPage = sectionPage && sectionPage.querySelector('script[data-dealers]');
          if (!sectionPage || !dataPage) throw new Error('Missing paginated section markup');

          const dealersPage = JSON.parse(dataPage.textContent || '[]');
          for (const dealer of dealersPage) {
            const id = Number(dealer.id);
            if (dealerIds.has(id)) continue;
            dealerIds.add(id);
            this._dealers.push(dealer);
          }

          sectionPage.querySelectorAll('[data-list] > .valco-sl__card').forEach((card) => {
            const id = Number(card.dataset.dealerId);
            if (cardIds.has(id)) return;
            cardIds.add(id);
            list.appendChild(document.importNode(card, true));
          });
        } catch (err) {
          failed = true;
          if (!this._alive(generation)) return;
          console.warn('[valco-google-store-locator] dealer page unavailable:', page);
        }
      }

      this.dataset.paginationState = failed ? 'partial' : 'complete';
      this.dataset.paginationLoaded = String(this._dealers.length);
    }

    _sortDealersAndCards() {
      const list = this.querySelector('[data-list]');
      if (!list) return;
      const locale = document.documentElement.lang || undefined;
      this._dealers.sort((a, b) => {
        const featuredOrder = Number(Boolean(b.featured)) - Number(Boolean(a.featured));
        if (featuredOrder !== 0) return featuredOrder;
        return String(a.name || '').localeCompare(String(b.name || ''), locale, {
          numeric: true,
          sensitivity: 'base',
        });
      });
      for (const dealer of this._dealers) {
        const card = this._cardsById.get(Number(dealer.id));
        if (card) list.appendChild(card);
      }
    }

    _maybeBootMap() {
      if (!this._ready || !this._nearViewport || !this.isConnected || this._map || this._mapLoading || this._mapUnavailable) return;
      const element = this.querySelector('[data-map]');
      if (!element || !element.getClientRects().length) return;
      if (window.matchMedia('(max-width: 1023px)').matches && !this.classList.contains('is-map')) return;
      this._bootMap();
    }

    async _bootMap() {
      const generation = this._generation;
      this._mapLoading = true;
      this.dataset.mapState = 'loading';
      try {
        const key = this.dataset.googleMapsKey;
        const mapId = this.dataset.googleMapId;
        const clusterUrl = this.dataset.clusterJs;
        if (!key || !mapId || mapId === 'DEMO_MAP_ID' || !clusterUrl) throw new Error('Map configuration unavailable');
        await Promise.all([loadGoogleMaps(key), loadScriptOnce(clusterUrl)]);
        if (!this._alive(generation) || this._mapUnavailable) return;
        if (!window.markerClusterer?.MarkerClusterer) throw new Error('Map clustering unavailable');
        this._initMap();
      } catch (_) {
        if (this._alive(generation)) this._handleMapUnavailable();
      } finally {
        if (this._alive(generation)) this._mapLoading = false;
      }
    }

    _handleMapUnavailable() {
      this._mapUnavailable = true;
      this.dataset.mapState = 'unavailable';
      this._disposeMap();
      const mapPane = this.querySelector('.valco-sl__pane--map');
      if (mapPane) mapPane.hidden = true;
      const tabs = this.querySelector('.valco-sl__tabs');
      if (tabs) tabs.hidden = true;
      this.querySelectorAll('[data-show-on-map]').forEach((button) => { button.hidden = true; });
      this._applyMobileView('list');
    }

    _initMap() {
      const maps = window.google.maps;
      const mapEl = this.querySelector('[data-map]');
      if (!mapEl) throw new Error('Map container unavailable');
      this._map = new maps.Map(mapEl, {
        center: coordinates(this.dataset.defaultLat, this.dataset.defaultLng) || { lat: 62.5, lng: 26 },
        zoom: Number(this.dataset.defaultZoom) || 5,
        mapId: this.dataset.googleMapId,
        gestureHandling: 'cooperative',
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
      const generation = this._generation;
      this._mapRenderTimer = setTimeout(() => {
        if (this._alive(generation)) this._handleMapUnavailable();
      }, 20000);
      this._mapListeners.push(maps.event.addListenerOnce(this._map, 'tilesloaded', () => {
        clearTimeout(this._mapRenderTimer);
        if (this._alive(generation) && !this._mapUnavailable) this.dataset.mapState = 'ready';
      }));
      this._infoWindow = new maps.InfoWindow();
      for (const dealer of this._dealers) {
        const position = dealer.online ? null : coordinates(dealer.lat, dealer.lng);
        if (!position) {
          const button = this._cardsById.get(Number(dealer.id))?.querySelector('[data-show-on-map]');
          if (button) button.hidden = true;
          continue;
        }
        const marker = new maps.marker.AdvancedMarkerElement({
          position,
          title: dealer.name || '',
          content: this._buildPinIcon(this.dataset.pinColor || '#171717'),
        });
        this._mapListeners.push(marker.addListener('click', () => {
          this._selectDealer(Number(dealer.id), { fromMap: true });
        }));
        this._markersById.set(Number(dealer.id), marker);
      }
      this._cluster = new window.markerClusterer.MarkerClusterer({
        map: this._map,
        markers: [],
        algorithm: new window.markerClusterer.SuperClusterAlgorithm({ maxZoom: 16, radius: 90 }),
        renderer: {
          render: ({ count, position }) => {
            const content = document.createElement('div');
            content.className = 'valco-gsl__cluster';
            content.textContent = String(count);
            Object.assign(content.style, {
              width: '44px', height: '44px', borderRadius: '50%',
              background: '#171717', color: '#fff', display: 'grid', placeItems: 'center',
              font: '700 14px "Maven Pro", sans-serif', border: '3px solid #fff',
              boxShadow: '0 1px 6px #0004', boxSizing: 'border-box',
            });
            return new maps.marker.AdvancedMarkerElement({
              position, content, title: String(count), zIndex: 1000 + count,
            });
          },
        },
      });
      // Filters may already have been changed while the API was loading.
      this._applyFilters();
      if (this.dataset.autoFit !== '0') {
        const bounds = new maps.LatLngBounds();
        this._visibleDealerIds.forEach((id) => {
          const marker = this._markersById.get(id);
          if (marker) bounds.extend(marker.position);
        });
        if (!bounds.isEmpty()) {
          this._map.fitBounds(bounds, 40);
          this._mapListeners.push(maps.event.addListenerOnce(this._map, 'idle', () => {
            if (this._map && !this._pendingSelection && !this._userLatLng && this._map.getZoom() > 11) this._map.setZoom(11);
          }));
        }
      }
      if (this._userLatLng) {
        this._map.setCenter(this._userLatLng);
        this._map.setZoom(9);
        this._addUserMarker(this._userLatLng);
      }
      if (this._pendingSelection != null) this._focusMarker(this._pendingSelection);
    }

    _buildPinIcon(color) {
      const content = document.createElement('div');
      content.className = 'valco-gsl__pin';
      content.style.width = '28px';
      content.style.height = '40px';
      content.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40" aria-hidden="true"><path d="M14 0C6.27 0 0 6.27 0 14c0 9.6 12.5 25 13.04 25.65a1.25 1.25 0 0 0 1.92 0C15.5 39 28 23.6 28 14c0-7.73-6.27-14-14-14Z"/><circle cx="14" cy="14" r="5.2" fill="#fff"/></svg>';
      content.querySelector('path').setAttribute('fill', color);
      return content;
    }

    _focusMarker(id) {
      const marker = this._markersById.get(id);
      const dealer = this._dealers.find((item) => Number(item.id) === id);
      if (!this._map || !marker || !dealer || !this._visibleDealerIds.has(id)) return;
      this._pendingSelection = id;
      this._map.panTo(marker.position);
      this._map.setZoom(Math.max(this._map.getZoom() || 0, 17));
      this._infoWindow.setContent(this._popupHtml(dealer));
      // An explicit position also works while the cluster is being recalculated.
      this._infoWindow.setPosition(marker.position);
      this._infoWindow.open({ map: this._map, shouldFocus: false });
    }

    _popupHtml(d) {
      const parts = [];
      parts.push('<strong>' + escapeHtml(d.name) + '</strong>');
      const line = [];
      if (d.address) line.push(escapeHtml(d.address));
      const cityLine = [d.postal, d.city].filter(Boolean).map(escapeHtml).join(' ');
      if (cityLine) line.push(cityLine);
      if (d.country && d.country !== 'Finland') line.push(escapeHtml(d.country));
      if (line.length) parts.push(line.join('<br>'));
      if (d.url && /^https?:\/\//i.test(String(d.url))) {
        const urlStr = String(d.url);
        const display = urlStr.replace(/^https?:\/\//, '').replace(/\/$/, '');
        parts.push('<a href="' + escapeHtml(urlStr) + '" target="_blank" rel="noopener nofollow">' + escapeHtml(display) + '</a>');
      }
      return '<div class="valco-gsl__popup">' + parts.join('<br>') + '</div>';
    }

    /* ── Event wiring ──────────────────────────────────────────── */

    _bindEvents() {
      const signal = this._controller.signal;
      const search = this.querySelector('[data-search]');
      if (search) {
        search.addEventListener('input', (e) => {
          this._searchTerm = (e.target.value || '').trim().toLowerCase();
          this._applyFilters();
        }, { signal });
      }

      const nearMe = this.querySelector('[data-near-me]');
      if (nearMe) nearMe.addEventListener('click', () => this._handleNearMe(), { signal });

      this.querySelectorAll('[data-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this._applyMobileView(btn.dataset.tab), { signal });
      });

      this.addEventListener('click', (e) => {
        const showBtn = e.target.closest('[data-show-on-map]');
        if (showBtn && this.contains(showBtn)) {
          e.preventDefault();
          e.stopPropagation();
          const id = Number(showBtn.dataset.showOnMap);
          this._selectDealer(id, { fromList: true });
          return;
        }
        const card = e.target.closest('.valco-sl__card');
        if (!card || !this.contains(card)) return;
        // Ignore clicks on inner links/buttons (phone, website, email, etc.)
        if (e.target.closest('a, button')) return;
        const id = Number(card.dataset.dealerId);
        this._selectDealer(id, { fromList: true });
      }, { signal });

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const card = e.target.closest && e.target.closest('.valco-sl__card');
        if (!card || !this.contains(card)) return;
        if (e.target.closest('a, button, input, select, textarea')) return;
        e.preventDefault();
        const id = Number(card.dataset.dealerId);
        this._selectDealer(id, { fromList: true });
      }, { signal });
    }

    /* ── Type-filter chips ─────────────────────────────────────── */

    _renderTypeChips() {
      const container = this.querySelector('[data-type-chips]');
      if (!container) return;

      const counts = new Map();
      for (const d of this._dealers) {
        const types = Array.isArray(d.types) ? d.types : [];
        for (const t of types) {
          if (typeof t !== 'string' || !t.trim()) continue;
          counts.set(t, (counts.get(t) || 0) + 1);
        }
      }
      const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

      const ICON_GLOBE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';
      const ICON_STORE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 9l1-5h16l1 5"/><path d="M5 9v11h14V9"/><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0"/></svg>';
      const ICON_HEADPHONES = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/></svg>';

      container.innerHTML = '';
      for (const [type] of sorted) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'valco-sl__chip';
        btn.dataset.type = type;
        btn.setAttribute('aria-pressed', this._activeTypes.has(type) ? 'true' : 'false');
        btn.title = type;
        btn.setAttribute('aria-label', type);
        const lower = type.toLowerCase();
        if (lower.indexOf('verkko') !== -1) {
          btn.innerHTML = ICON_GLOBE;
        } else if (lower.indexOf('myym') !== -1) {
          btn.innerHTML = ICON_STORE;
        } else {
          btn.textContent = type;
        }
        btn.addEventListener('click', () => {
          if (this._activeTypes.has(type)) {
            this._activeTypes.delete(type);
            btn.setAttribute('aria-pressed', 'false');
          } else {
            this._activeTypes.add(type);
            btn.setAttribute('aria-pressed', 'true');
          }
          this._applyFilters();
        }, { signal: this._controller.signal });
        container.appendChild(btn);
      }

      // Fitting-availability chip — separate filter dimension. Only render it
      // when at least one dealer actually offers try-on, so the control stays
      // hidden until that data is collected.
      if (this._dealers.some((d) => d.fitting === true)) {
        const label = this.dataset.fittingLabel || '';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'valco-sl__chip valco-sl__chip--fitting';
        btn.dataset.fitting = '1';
        btn.setAttribute('aria-pressed', this._fittingActive ? 'true' : 'false');
        if (label) {
          btn.title = label;
          btn.setAttribute('aria-label', label);
        }
        btn.innerHTML = ICON_HEADPHONES;
        btn.addEventListener('click', () => {
          this._fittingActive = !this._fittingActive;
          btn.setAttribute('aria-pressed', this._fittingActive ? 'true' : 'false');
          this._applyFilters();
        }, { signal: this._controller.signal });
        container.appendChild(btn);
      }
    }

    /* ── Filtering ─────────────────────────────────────────────── */

    _applyFilters() {
      const term = this._searchTerm;
      const types = this._activeTypes;
      let visible = 0;
      const markers = [];
      this._visibleDealerIds.clear();

      for (const d of this._dealers) {
        const card = this._cardsById.get(Number(d.id));
        if (!card) continue;
        const hay = (
          (d.name || '') + ' ' +
          (d.city || '') + ' ' +
          (d.postal || '') + ' ' +
          (d.address || '') + ' ' +
          (Array.isArray(d.types) ? d.types.join(' ') : '')
        ).toLowerCase();

        const matchesSearch = term === '' || hay.indexOf(term) !== -1;
        // AND-logic: when multiple chips are active, dealer must have ALL of them.
        const dealerTypes = Array.isArray(d.types) ? d.types : [];
        let matchesType = true;
        if (types.size > 0) {
          for (const t of types) {
            if (!dealerTypes.includes(t)) { matchesType = false; break; }
          }
        }
        const matchesFitting = !this._fittingActive || d.fitting === true;
        const show = matchesSearch && matchesType && matchesFitting;

        card.classList.toggle('is-hidden', !show);
        if (show) visible++;

        if (show) {
          this._visibleDealerIds.add(Number(d.id));
          const marker = this._markersById.get(Number(d.id));
          if (marker) markers.push(marker);
        }
      }
      if (this._cluster) {
        this._cluster.clearMarkers(true);
        this._cluster.addMarkers(markers, true);
        this._cluster.render();
      }
      if (this._activeId != null && !this._visibleDealerIds.has(this._activeId)) {
        this._infoWindow?.close();
        this._pendingSelection = null;
      }

      this._updateCount(visible);
      if (this._emptyNode) this._emptyNode.hidden = visible !== 0;
    }

    _updateCount(n) {
      if (!this._countNode || !this._countTemplate) return;
      // Locale string is e.g. "{{ count }} jälleenmyyjää" — Liquid pre-renders the
      // initial count, so we restore that for the all-dealers case and replace
      // with a digit otherwise. A simple regex swap is enough for our keys.
      const replaced = this._countTemplate.replace(/\d+/, String(n));
      this._countNode.textContent = replaced;
    }

    /* ── Selection / list↔map sync ─────────────────────────────── */

    _selectDealer(id, opts) {
      opts = opts || {};
      if (this._activeId === id && !opts.fromMap && !opts.fromList) return;

      const prevCard = this._activeId != null ? this._cardsById.get(this._activeId) : null;
      if (prevCard) {
        prevCard.classList.remove('is-active');
        prevCard.setAttribute('aria-pressed', 'false');
      }

      this._activeId = id;
      const card = this._cardsById.get(id);
      const marker = this._markersById.get(id);

      if (card) {
        card.classList.add('is-active');
        card.setAttribute('aria-pressed', 'true');
        if (opts.fromMap) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      if (opts.fromList) {
        const dealer = this._dealers.find((item) => Number(item.id) === id);
        if (!dealer || dealer.online || !coordinates(dealer.lat, dealer.lng)) return;
        this._pendingSelection = id;
        if (!this._mapUnavailable) this._applyMobileView('map');
        if (this._map) this._focusMarker(id);
      } else if (opts.fromMap && marker && this._map) {
        const dealer = this._dealers.find((item) => Number(item.id) === id);
        this._pendingSelection = id;
        this._infoWindow.setContent(this._popupHtml(dealer));
        this._infoWindow.open({ map: this._map, anchor: marker, shouldFocus: false });
      }
    }

    /* ── Near me ───────────────────────────────────────────────── */

    _handleNearMe() {
      if (!('geolocation' in navigator)) return;
      const generation = this._generation;
      const btn = this.querySelector('[data-near-me]');
      if (btn) btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!this._alive(generation)) return;
          this._pendingSelection = null;
          if (btn) btn.disabled = false;
          const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          if (!isFinite(ll.lat) || !isFinite(ll.lng)) return;
          this._userLatLng = ll;
          this._sortByDistanceFrom(ll);
          if (this._map) {
            this._infoWindow?.close();
            this._map.setCenter(ll);
            this._map.setZoom(9);
            this._addUserMarker(ll);
          }
          if (window.matchMedia('(max-width: 1023px)').matches) {
            this._applyMobileView('map');
          }
        },
        () => {
          if (!this._alive(generation)) return;
          if (btn) btn.disabled = false;
          console.warn('[valco-google-store-locator] geolocation unavailable');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }

    _sortByDistanceFrom(ll) {
      const list = this.querySelector('[data-list]');
      if (!list) return;

      const items = Array.from(list.children);
      const decorated = items.map((card) => {
        const dealer = this._dealers.find((item) => Number(item.id) === Number(card.dataset.dealerId));
        const position = dealer?.online ? null : coordinates(card.dataset.lat, card.dataset.lng);
        const km = position ? haversineKm(ll, position) : Infinity;
        return { card, km };
      });
      decorated.sort((a, b) => a.km - b.km);

      const frag = document.createDocumentFragment();
      for (const { card, km } of decorated) {
        const dist = card.querySelector('[data-distance]');
        if (dist) {
          if (isFinite(km)) {
            dist.textContent = formatDistance(km);
            dist.hidden = false;
          } else {
            dist.hidden = true;
          }
        }
        frag.appendChild(card);
      }
      list.appendChild(frag);
    }

    _addUserMarker(ll) {
      if (!this._map) return;
      if (this._userMarker) { this._userMarker.position = ll; return; }
      const content = document.createElement('div');
      content.className = 'valco-gsl__user-marker';
      content.setAttribute('aria-hidden', 'true');
      Object.assign(content.style, {
        width: '16px', height: '16px', borderRadius: '50%', background: '#3578e5',
        border: '3px solid #fff', boxShadow: '0 0 0 3px #3578e544', boxSizing: 'border-box',
      });
      this._userMarker = new window.google.maps.marker.AdvancedMarkerElement({
        map: this._map, position: ll, content, zIndex: 2000,
      });
    }

    /* ── Mobile list/map tabs ──────────────────────────────────── */

    _applyMobileView(view) {
      if (this._mapUnavailable) view = 'list';
      this.classList.toggle('is-list', view === 'list');
      this.classList.toggle('is-map', view === 'map');
      this.querySelectorAll('[data-tab]').forEach((b) => {
        b.setAttribute('aria-selected', b.dataset.tab === view ? 'true' : 'false');
      });
      if (view === 'map') {
        const generation = this._generation;
        requestAnimationFrame(() => {
          if (!this._alive(generation)) return;
          this._maybeBootMap();
          if (this._map && this._pendingSelection != null) this._focusMarker(this._pendingSelection);
        });
      }
    }
  }

  customElements.define('valco-google-store-locator', ValcoGoogleStoreLocator);
})();
